import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.js'
import { FormModal } from '../../components/FormModal.js'
import { ErrorState } from '../../components/ErrorState.js'
import { LoadingState } from '../../components/LoadingState.js'
import { Button, Input, Text } from '../../ui/index.js'

interface DepartmentRow {
  readonly id: string
  readonly name: string
  readonly createdAt: string
}

interface DepartmentUser {
  readonly departmentId: string | null
}

interface DeleteResponse {
  readonly id: string
  readonly unassignedUsers: number
}

function formatCreatedAt(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '–'
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function peopleLabel(count: number): string {
  return count === 1 ? 'person' : 'people'
}

function memberCounts(users: readonly DepartmentUser[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const user of users) {
    if (user.departmentId === null) continue
    counts.set(user.departmentId, (counts.get(user.departmentId) ?? 0) + 1)
  }
  return counts
}

export function DepartmentsTab() {
  const { api } = useAuth()
  const queryClient = useQueryClient()
  const [createName, setCreateName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [savedLine, setSavedLine] = useState<string | null>(null)
  const [editing, setEditing] = useState<DepartmentRow | null>(null)
  const [editName, setEditName] = useState('')
  const [editError, setEditError] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<DepartmentRow | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const departmentsQuery = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: () => api.get<DepartmentRow[]>('/api/departments'),
  })

  const usersQuery = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api.get<DepartmentUser[]>('/api/users'),
  })

  const createDepartment = useMutation({
    mutationFn: (name: string) => api.post<DepartmentRow>('/api/departments', { name }),
    onSuccess: async () => {
      setCreateName('')
      setCreateError(null)
      await queryClient.invalidateQueries({ queryKey: ['admin', 'departments'] })
    },
    onError: (error) => {
      setCreateError(error instanceof ApiError ? error.message : 'Could not add that department.')
    },
  })

  const renameDepartment = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api.patch<DepartmentRow>(`/api/departments/${input.id}`, { name: input.name }),
    onSuccess: async () => {
      setEditing(null)
      setEditError(null)
      await queryClient.invalidateQueries({ queryKey: ['admin', 'departments'] })
    },
    onError: (error) => {
      setEditError(error instanceof ApiError ? error.message : 'Could not rename that department.')
    },
  })

  const deleteDepartment = useMutation({
    mutationFn: (input: DepartmentRow) => api.del<DeleteResponse>(`/api/departments/${input.id}`),
    onSuccess: async (result, input) => {
      setDeleting(null)
      setDeleteError(null)
      setSavedLine(`Deleted ${input.name}. ${result.unassignedUsers} people unassigned.`)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin', 'departments'] }),
        queryClient.invalidateQueries({ queryKey: ['admin', 'users'] }),
      ])
    },
    onError: (error) => {
      setDeleteError(error instanceof ApiError ? error.message : 'Could not delete that department.')
    },
  })

  const counts = useMemo(
    () => memberCounts(usersQuery.data ?? []),
    [usersQuery.data],
  )

  const loading = departmentsQuery.isPending || usersQuery.isPending

  if (loading) {
    return <LoadingState label="Loading departments" />
  }

  if (departmentsQuery.isError || usersQuery.isError) {
    return (
      <ErrorState
        error={departmentsQuery.error ?? usersQuery.error}
        onRetry={() => void Promise.all([departmentsQuery.refetch(), usersQuery.refetch()])}
      />
    )
  }

  const departments = departmentsQuery.data ?? []
  const editingName = editing === null ? '' : editName.trim()
  const canRename = editing !== null && editingName !== '' && editingName !== editing.name.trim()
  const deletingCount = deleting === null ? 0 : counts.get(deleting.id) ?? 0

  return (
    <div style={{ display: 'grid', gap: 'var(--o-space-5)' }}>
      <section
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--o-space-4)',
        }}
      >
        <form
          onSubmit={(event) => {
            event.preventDefault()
            const name = createName.trim()
            if (name === '') return
            void createDepartment.mutateAsync(name).catch(() => undefined)
          }}
          style={{ display: 'grid', gap: 'var(--o-space-3)' }}
        >
          <div style={{ display: 'grid', gap: 'var(--o-space-2)', maxWidth: 360 }}>
            <label className="field-label" htmlFor="department-name">
              Department name
            </label>
            <Input
              id="department-name"
              aria-label="Department name"
              value={createName}
              onChange={(value) => {
                setCreateName(value)
                setCreateError(null)
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--o-space-3)', flexWrap: 'wrap' }}>
            <Button kind="primary" type="submit" disabled={createDepartment.isPending || createName.trim() === ''}>
              Add department
            </Button>
            {createError !== null ? <Text color="secondary">{createError}</Text> : null}
            {savedLine !== null ? <Text color="secondary">{savedLine}</Text> : null}
          </div>
        </form>
      </section>

      <section
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)',
          overflowX: 'auto', overflowY: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Department</th>
              <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Members</th>
              <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Created</th>
              <th style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {departments.map((department) => {
              const count = counts.get(department.id) ?? 0
              return (
                <tr key={department.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: 'var(--o-space-3) var(--o-space-4)', fontWeight: 500 }}>{department.name}</td>
                  <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>
                    {count} {peopleLabel(count)}
                  </td>
                  <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>{formatCreatedAt(department.createdAt)}</td>
                  <td style={{ padding: 'var(--o-space-3) var(--o-space-4)' }}>
                    <div style={{ display: 'flex', gap: 'var(--o-space-2)', flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        onClick={() => {
                          setEditing(department)
                          setEditName(department.name)
                          setEditError(null)
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        kind="tertiary"
                        onClick={() => {
                          setDeleting(department)
                          setDeleteError(null)
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </section>

      <FormModal
        title={editing === null ? 'Edit department' : `Edit ${editing.name}`}
        isOpen={editing !== null}
        onClose={() => {
          setEditing(null)
          setEditError(null)
        }}
        onSubmit={() => {
          if (editing === null) return
          void renameDepartment.mutateAsync({ id: editing.id, name: editName.trim() }).catch(() => undefined)
        }}
        submitLabel="Save changes"
        canSubmit={canRename}
        busy={renameDepartment.isPending}
      >
        <div className="field" style={{ marginBottom: 'var(--o-space-4)' }}>
          <label className="field-label" htmlFor="department-rename-name">
            Department name
          </label>
          <Input
            id="department-rename-name"
            aria-label="Department name"
            value={editName}
            onChange={(value) => {
              setEditName(value)
              setEditError(null)
            }}
          />
        </div>
        {editError !== null ? <Text color="secondary">{editError}</Text> : null}
      </FormModal>

      <FormModal
        title={deleting === null ? 'Delete department' : `Delete ${deleting.name}`}
        isOpen={deleting !== null}
        onClose={() => {
          setDeleting(null)
          setDeleteError(null)
        }}
        onSubmit={() => {
          if (deleting === null) return
          void deleteDepartment.mutateAsync(deleting).catch(() => undefined)
        }}
        submitLabel="Delete"
        canSubmit={deleting !== null}
        busy={deleteDepartment.isPending}
      >
        <div style={{ display: 'grid', gap: 'var(--o-space-3)' }}>
          <Text color="secondary">
            {deletingCount} {peopleLabel(deletingCount)} will be left without a department.
          </Text>
          {deleteError !== null ? <Text color="secondary">{deleteError}</Text> : null}
        </div>
      </FormModal>
    </div>
  )
}
